import React from 'react';
import { 
  TrendingUp, 
  Target, 
  PieChart, 
  BarChart2, 
  Shield, 
  Globe 
} from 'lucide-react';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов инвестиций в ИИ
 */
const ResultsOverview = ({ results }) => {
  // Если нет результатов, не отображаем компонент
  if (!results) return null;

  // Определение риск-категории
  const getRiskCategory = (riskLevel) => {
    const categories = {
      'low': { name: 'Низкий риск', color: 'text-green-600 dark:text-green-400' },
      'moderate': { name: 'Умеренный риск', color: 'text-yellow-600 dark:text-yellow-400' },
      'high': { name: 'Высокий риск', color: 'text-red-600 dark:text-red-400' }
    };
    return categories[riskLevel] || { name: 'Неопределен', color: 'text-gray-600 dark:text-gray-400' };
  };

  // Маппинг секторов
  const getSectorName = (sectorId) => {
    const sectorMap = {
      'software': 'Программное обеспечение',
      'hardware': 'Аппаратное обеспечение',
      'cloud': 'Облачные технологии',
      'robotics': 'Робототехника'
    };
    return sectorMap[sectorId] || sectorId;
  };

  // Расчет доли секторов и технологий
  const calculateSectorDistribution = () => {
    const totalSectors = results.aiSectors.length;
    if (totalSectors === 0) return [];
    
    return results.aiSectors.map(sector => ({
      name: getSectorName(sector),
      percentage: (1 / totalSectors) * 100
    }));
  };

  // Маппинг типов инвестиций
  const getInvestmentTypeName = (typeId) => {
    const typeMap = {
      'diversified': 'Диверсифицированная',
      'focused': 'Целевая',
      'etf': 'ETF ИИ'
    };
    return typeMap[typeId] || typeId;
  };

  // Категория риск-доходности
  const riskCategory = getRiskCategory(results.riskLevel);

  // Распределение по секторам
  const sectorDistribution = calculateSectorDistribution();

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-6 mb-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center">
        <TrendingUp className="w-6 h-6 mr-3 text-indigo-500" />
        Результаты инвестиционного плана
      </h2>

      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* Начальная и итоговая сумма */}
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 p-5 rounded-lg border border-indigo-100 dark:border-indigo-800/50">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-base font-medium text-indigo-700 dark:text-indigo-300">
              Инвестиционный рост
            </h3>
            <Target className="w-5 h-5 text-indigo-500" />
          </div>
          <div className="space-y-2">
            <p className="text-sm text-gray-600 dark:text-gray-400">Начальная сумма</p>
            <p className="text-xl font-bold text-gray-900 dark:text-white">
              {formatCurrency(results.initialInvestment, 0)}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Итоговый баланс</p>
            <p className="text-xl font-bold text-green-600 dark:text-green-400">
              {formatCurrency(results.finalBalance, 0)}
            </p>
          </div>
        </div>

        {/* Доходность и риски */}
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 p-5 rounded-lg border border-green-100 dark:border-green-800/50">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-base font-medium text-green-700 dark:text-green-300">
              Доходность и риски
            </h3>
            <Shield className={`w-5 h-5 ${riskCategory.color}`} />
          </div>
          <div className="space-y-2">
            <p className="text-sm text-gray-600 dark:text-gray-400">Средняя годовая доходность</p>
            <p className="text-xl font-bold text-green-600 dark:text-green-400">
              {formatNumber(results.averageAnnualReturn, 2)}%
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Уровень риска</p>
            <p className={`text-xl font-bold ${riskCategory.color}`}>
              {riskCategory.name}
            </p>
          </div>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Распределение по секторам */}
        <div className="bg-gray-50 dark:bg-gray-700/30 p-5 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-base font-medium text-gray-900 dark:text-white">
              Секторы ИИ
            </h3>
            <PieChart className="w-5 h-5 text-indigo-500" />
          </div>
          {sectorDistribution.length > 0 ? (
            <ul className="space-y-2">
              {sectorDistribution.map((sector, index) => (
                <li key={index} className="flex justify-between items-center">
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {sector.name}
                  </span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {formatNumber(sector.percentage, 1)}%
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Нет выбранных секторов ИИ
            </p>
          )}
        </div>

        {/* Географический и технологический фокус */}
        <div className="bg-blue-50 dark:bg-blue-900/20 p-5 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-base font-medium text-gray-900 dark:text-white">
              Охват инвестиций
            </h3>
            <Globe className="w-5 h-5 text-indigo-500" />
          </div>
          <div className="space-y-3">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                Технологический фокус
              </p>
              <div className="flex flex-wrap gap-2">
                {results.technologyFocus && results.technologyFocus.length > 0 ? (
                  results.technologyFocus.map((tech, index) => (
                    <span 
                      key={index} 
                      className="px-2 py-1 rounded-md bg-indigo-100 dark:bg-indigo-900/50 text-xs text-indigo-700 dark:text-indigo-300"
                    >
                      {tech}
                    </span>
                  ))
                ) : (
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Не указано
                  </span>
                )}
              </div>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                Географический фокус
              </p>
              <div className="flex flex-wrap gap-2">
                {results.geographicFocus && results.geographicFocus.length > 0 ? (
                  results.geographicFocus.map((region, index) => (
                    <span 
                      key={index} 
                      className="px-2 py-1 rounded-md bg-green-100 dark:bg-green-900/50 text-xs text-green-700 dark:text-green-300"
                    >
                      {region}
                    </span>
                  ))
                ) : (
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Не указано
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Итоговая статистика */}
      <div className="mt-6 bg-gray-50 dark:bg-gray-800/50 p-5 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-center mb-3">
          <BarChart2 className="w-5 h-5 mr-3 text-indigo-500" />
          <h3 className="text-base font-medium text-gray-900 dark:text-white">
            Статистика инвестиций
          </h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">Общий взнос</p>
            <p className="text-base font-semibold text-gray-900 dark:text-white">
              {formatCurrency(results.totalContributions, 0)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">Доход от инвестиций</p>
            <p className="text-base font-semibold text-green-600 dark:text-green-400">
              {formatCurrency(results.totalInterest, 0)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">Тип стратегии</p>
            <p className="text-base font-semibold text-gray-900 dark:text-white">
              {getInvestmentTypeName(results.investmentType)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">Период</p>
            <p className="text-base font-semibold text-gray-900 dark:text-white">
              {results.investmentTerm} лет
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultsOverview;